"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const lil_uri_1 = __importDefault(require("lil-uri"));
const log_1 = require("../../../log");
const UrlHelper_1 = require("./UrlHelper");
const ObjectHelper_1 = require("../../objects/ObjectHelper");
const log = (0, log_1.getLog)(module);
const portRegex = new RegExp(/(.*)(:\d+$)/);
/**
 * Builder for manipulating URLs, uses the {@code lil-uri} library
 * to parse and build URLs.
 *
 * @author Adam Ayres
 *
 * @deprecated Use NextGenUrlBuilder instead. If NextGenUrlBuilder does not meet your needs,
 * please port the necessary functionality to NextGenUrlBuilder.
 */
class UrlBuilder {
    static fromUrl(url) {
        return new UrlBuilder(url);
    }
    constructor(url) {
        this.uri = (0, lil_uri_1.default)(url);
    }
    /**
     * Add's key/value pairs to the path of a URL. Only accepts an even number of
     * arguments, otherwise will do nothing. Keys that already exists in the path
     * of the URL will be updates.
     *
     * @param keyValuePairs key value pairs to add to the path
     * @returns {UrlBuilder} this builder
     */
    addPathItems(...keyValuePairs) {
        const currentPathParts = this.uri.path().split('/');
        if (keyValuePairs.length % 2 === 0) {
            for (let i = 0; i < keyValuePairs.length; i += 2) {
                const key = keyValuePairs[i];
                const value = keyValuePairs[i + 1];
                if (key && value) {
                    const keyIndex = currentPathParts.indexOf(key);
                    if (keyIndex > -1 && keyIndex + 1 < currentPathParts.length) {
                        currentPathParts.splice(keyIndex + 1, 1, value);
                    }
                    else {
                        currentPathParts.push(key, value);
                    }
                }
                else {
                    log.warn('UrlManager#addPath expects key value pairs to be valid values, found key: [%s], value: [%s]', key, value);
                }
            }
        }
        else {
            log.warn('UrlHelper#addPath expects key value pairs passed in for path parameters');
        }
        this.uri.path(currentPathParts.join('/'));
        return this;
    }
    /**
     * Appends a path to an existing URL that may contain an existing path.
     * @param path - the path to append
     */
    addPath(path) {
        if (!path) {
            return this;
        }
        const pathExcludingQueryParam = path.includes('?') ? path.slice(0, path.indexOf('?')) : path;
        const transformedPath = [...this.uri.path().split('/'), ...pathExcludingQueryParam.split('/')]
            .filter(item => item.trim().length > 0)
            .join('/');
        const updatedPath = !path.includes('?')
            ? transformedPath
            : `${transformedPath}${path.slice(path.indexOf('?'))}`;
        this.uri.path(updatedPath.length > 0 ? `/${updatedPath}` : '');
        return this;
    }
    /**
     * Removes a key and it's associated value from a URL path. We treat path parameters
     * as key/values where the "key" path value precedes the "value" path value. If the key
     * does not exist then nothing will happen.
     *
     * @param key the key and it's associated value to remove from the URL
     * @returns {UrlBuilder} this builder
     */
    removePathItem(key) {
        const currentPathParts = this.uri.path().split('/');
        const keyIndex = currentPathParts.indexOf(key);
        if (keyIndex > -1 && keyIndex + 1 < currentPathParts.length) {
            currentPathParts.splice(keyIndex, 2);
        }
        else {
            log.trace('Did not find path param in URL to remove, key: [%s]', key);
        }
        this.uri.path(currentPathParts.join('/'));
        return this;
    }
    /**
     * Removes a index value from a URL path.
     *
     * @param index - the index to remove from the URL
     * @returns {UrlBuilder} this builder
     */
    removePathItemByIndex(index) {
        const currentPathParts = this.uri.path().split('/');
        this.uri.path(`/${currentPathParts.splice(index, 1).join('/')}`);
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    encodeQueryParameters(queryParameters) {
        const encodedQueryParameters = {};
        Object.entries(queryParameters).forEach(([key, value]) => {
            const encodedKey = (0, UrlHelper_1.safeEncodeUriComponent)(key);
            if (Array.isArray(value)) {
                const encodedValue = value
                    .filter(item => item !== undefined && item !== null)
                    .map(item => (0, UrlHelper_1.safeEncodeUriComponent)(item));
                if (encodedValue.length > 0) {
                    encodedQueryParameters[encodedKey] = encodedValue;
                }
            }
            else if (value !== undefined && value !== null) {
                encodedQueryParameters[encodedKey] = (0, UrlHelper_1.safeEncodeUriComponent)(value);
            }
        });
        return encodedQueryParameters;
    }
    buildQueryParameters(current, queryParameters, appendExisting, encodeQueryParameters = true) {
        const currentQuery = this.uri.query() || {};
        const encodedQueryParameters = encodeQueryParameters
            ? this.encodeQueryParameters(queryParameters)
            : queryParameters;
        let finalQuery = Object.assign({}, current);
        if (appendExisting) {
            Object.keys(encodedQueryParameters).forEach(queryKey => {
                if (currentQuery[queryKey]) {
                    const currentValue = currentQuery[queryKey];
                    const valueToAdd = encodedQueryParameters[queryKey];
                    const currentValueAsArray = Array.isArray(currentValue) ? currentValue : [currentValue];
                    const valueToAddAsArray = Array.isArray(valueToAdd) ? valueToAdd : [valueToAdd];
                    finalQuery[queryKey] = [...currentValueAsArray, ...valueToAddAsArray];
                }
                else {
                    finalQuery[queryKey] = encodedQueryParameters[queryKey];
                }
            });
        }
        else {
            finalQuery = Object.assign(Object.assign({}, currentQuery), encodedQueryParameters);
        }
        if (!(0, ObjectHelper_1.isObjectEmpty)(finalQuery)) {
            this.uri.query(finalQuery);
        }
    }
    /**
     * Add query params to an existing URL.
     *
     * @param queryParameters - the query params as an object map
     * @param appendExisting - whether to append query params if they already exist on the URL
     * @param encodeQueryParameters - whether to encode the query params
     */
    addQuery(queryParameters, appendExisting = true, encodeQueryParameters = true) {
        const currentQuery = this.uri.query() || {};
        this.buildQueryParameters(Object.assign({}, currentQuery), queryParameters, appendExisting, encodeQueryParameters);
        return this;
    }
    /**
     * Sets the port
     *
     * @param port the port to set
     */
    port(port) {
        if (this.uri.host()) {
            if (portRegex.test(this.uri.host())) {
                this.uri.host(this.uri.host().replace(portRegex, `$1:${port}`));
            }
            else {
                this.uri.host(`${this.uri.host()}${port ? `:${port}` : ''}`);
            }
        }
        else {
            this.uri.port(port);
        }
        return this;
    }
    /**
     * Replace query params on an existing URL.
     *
     * @param queryParameters - the query params as an object map
     * @param appendExisting - whether to append query params if they already exist on the URL
     * @param encodeQueryParameters - whether to encode the query params
     */
    replaceQuery(queryParameters, appendExisting = true, encodeQueryParameters = true) {
        this.buildQueryParameters({}, queryParameters, appendExisting, encodeQueryParameters);
        return this;
    }
    /**
     * Removes a query param key/value
     *
     * @param key - the key of the query param value pair to remove
     */
    removeQuery(key) {
        const currentQuery = this.uri.query() || {};
        delete currentQuery[key];
        this.uri.query(currentQuery);
        return this;
    }
    /**
     * The relative URL contains the path, query, and hash.
     */
    relativeUrl() {
        const path = this.uri.path();
        const query = this.uri.search();
        const hash = this.uri.hash();
        return `${path}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
    }
    build() {
        return this.uri.build();
    }
}
exports.default = UrlBuilder;
//# sourceMappingURL=UrlBuilder.js.map